import { API_CUSTOM_HEADERS, ConversationMessageDto, ReceiveChatConversationMessagesDto } from '@endion/types';
import { getEndpoint } from '../utils/api.utils';
import {
  getContactKeyFromLocalStorage,
  getConversationKeyFromLocalStorage,
  saveContactKeyToLocalStorage,
  saveConversationKeyInLocalStorage,
} from '../utils/local-storage';

export async function sendChatMessage({
  messages,
  chatbotApiKey,
  context,
}: {
  messages: ConversationMessageDto[];
  chatbotApiKey: string;
  context?: ReceiveChatConversationMessagesDto['context'];
}): Promise<{
  reader: ReadableStreamDefaultReader<Uint8Array>;
  backendIncomingMessageId: string;
}> {
  const headers = new Headers();

  const contactKey = getContactKeyFromLocalStorage();
  if (contactKey) headers.append(API_CUSTOM_HEADERS.X_CHAT_CONTACT_KEY, contactKey);

  const conversationKey = getConversationKeyFromLocalStorage();
  if (conversationKey) headers.append(API_CUSTOM_HEADERS.X_CHAT_CONVERSATION_KEY, conversationKey);

  headers.append(API_CUSTOM_HEADERS.X_CHATBOT_API_KEY, chatbotApiKey || '');
  headers.append(API_CUSTOM_HEADERS.X_SIMULATED_ORIGIN, window.endion_debug?.simulatedOrigin || '');
  headers.append(API_CUSTOM_HEADERS.X_CHAT_FULL_URL, window.location.href);
  headers.append(API_CUSTOM_HEADERS.X_REAL_ESTATE_PROPERTY_ID, context?.realEstate?.propertyId || '');
  headers.append(API_CUSTOM_HEADERS.X_REAL_ESTATE_DEVELOPMENT_ID, context?.realEstate?.developmentId || '');
  headers.append('Content-Type', 'application/json');

  const response = await fetch(getEndpoint('conversation/chat'), {
    method: 'POST',
    headers,
    // FIX: this is duplicated, we are sending the context here and in the headers
    body: JSON.stringify({ messages, context }),
  });

  if (!response.body) {
    throw new Error('No response body');
  }

  // If the response contains an exception, try to read the JSON payload
  if (!response.ok) {
    const data = await response.json();
    const apiError = new Error();

    Object.assign(apiError, data);

    throw apiError;
  }

  // Conversation key is returned on each chat request
  saveConversationKeyInLocalStorage(response.headers.get(API_CUSTOM_HEADERS.X_CHAT_CONVERSATION_KEY) || '');

  const responseChatContactkey = response.headers.get(API_CUSTOM_HEADERS.X_CHAT_CONTACT_KEY);

  // if there was an error on the response, the headers are empty
  // so we need to check if the responseChatContactkey is not null
  if (responseChatContactkey) {
    saveContactKeyToLocalStorage(responseChatContactkey);
  }

  const backendIncomingMessageId = response.headers.get(API_CUSTOM_HEADERS.X_CHAT_RESPONSE_MESSAGE_ID);

  if (!backendIncomingMessageId) {
    throw new Error('No backend incoming message id');
  }

  return {
    reader: response.body.getReader(),
    backendIncomingMessageId,
  };
}
